import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, Loader2, MapPin } from 'lucide-react';
import Seo from '../../components/Seo';
import { SEO_CATEGORY_LABEL, SEO_CATEGORIES } from './communitySeoData';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResourceCategoryLanding = () => {
  const { seg1: categorySlug } = useParams();
  const label = SEO_CATEGORY_LABEL[categorySlug] || 'Community Resources';
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API_URL}/community-resources/seo/config`)
      .then((r) => { if (active) setStates(r.data.states || []); })
      .catch(() => { if (active) setStates([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://ineedasocialworker.com';
  const breadcrumb = {
    '@context': 'https://schema.org', '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
      { '@type': 'ListItem', position: 2, name: 'Community Resources', item: `${origin}/community/resources` },
      { '@type': 'ListItem', position: 3, name: label, item: `${origin}/community-resources/${categorySlug}` },
    ],
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20" data-testid="cr-category-landing">
      <Seo
        title={`${label} by State`}
        description={`Find ${label.toLowerCase()} programs and resources in all 50 states and Washington, DC. Get connected to local help through 2-1-1 and state agencies.`}
        path={`/community-resources/${categorySlug}`}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      </Helmet>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <nav className="flex items-center flex-wrap gap-1 text-sm text-gray-500 mb-6">
          <Link to="/" className="hover:text-primary">Home</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <Link to="/community/resources" className="hover:text-primary">Community Resources</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-gray-900 font-medium">{label}</span>
        </nav>

        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-gray-900" data-testid="cr-category-h1">
          {label} Resources by State
        </h1>
        <p className="mt-4 text-gray-700 max-w-3xl">
          Select your state to find {label.toLowerCase()} programs, eligibility guidance, and verified
          local and nationwide resources. Every state guide includes how to connect through 2-1-1 and the
          relevant state agencies.
        </p>

        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>
        ) : (
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="cr-state-grid">
            {states.map((s) => (
              <Link
                key={s.slug}
                to={`/community-resources/${categorySlug}/${s.slug}`}
                className="bg-white rounded-lg border border-gray-100 px-4 py-3 text-sm font-medium text-gray-800 hover:border-primary hover:text-primary flex items-center gap-2 transition-colors"
                data-testid="cr-state-link"
              >
                <MapPin className="w-3.5 h-3.5 text-gray-400" /> {s.name}
              </Link>
            ))}
          </div>
        )}

        {/* Related categories */}
        <div className="mt-12" data-testid="cr-other-categories">
          <h2 className="text-base font-semibold text-gray-900 mb-3">Other resource guides</h2>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {SEO_CATEGORIES.filter((c) => c.slug !== categorySlug).map((c) => (
              <Link key={c.slug} to={`/community-resources/${c.slug}`} className="text-sm text-primary hover:underline">{c.label}</Link>
            ))}
          </div>
        </div>

        <div className="mt-10">
          <Link to="/community/resources" className="text-primary font-semibold">← Back to the full Community Resource Directory</Link>
        </div>
      </div>
    </div>
  );
};

export default ResourceCategoryLanding;
